// ═══════════════════════════════════════════════════════════════════
// fieldBeings3D.jsx — SHIP 8 · THE STAND
//   Terror's half of the being bargain: Annihilation builds the body,
//   the Shatterfield decides how it STANDS on a rift.
// ═══════════════════════════════════════════════════════════════════
// caP'n, 2026-09-15: "I want the Shatterfield to have the character
// Models from Annihilation, let's see Spectral Tiger Beb on the field
// when it is summoned! … true 3D battling."
//
// ── THE LAW THIS FILE EXISTS TO PROTECT (ie-being-models §0b) ──────
//  `ieBeingModels.jsx` is CROSS-GAME CANONICAL. It is vendored into
//  this app BYTE-IDENTICAL to gamemaster/models/ieBeingModels.jsx and
//  it is never edited here. Not one line, not one colour, not one
//  number. If a being looks wrong on the felt, the fix lands in THIS
//  file or it doesn't land. They own the body, we own the pose.
//
//  ⚠ Terror is EXEMPT from broad model sweeps (MODELS.md §3). This is
//  not a sweep — caP'n asked for Annihilation's bodies in the duel by
//  name. A kit re-vendor into Terror is still a Terror decision, made
//  here, never as part of an all-titles pass.
//
// ── THE RENDERER CONTRACT (Shatterfield3D's own law) ──────────────
//  Pure renderer. Reads a card id and a position; writes nothing back.
//  No game logic, no Firebase, no rules. Delete this file and every
//  rift falls back to the card slab it showed before SHIP 8.
//
// ── WHAT IT DOES ──────────────────────────────────────────────────
//  · beingOf(cardId)  — card id → { lineage, tier } for the 36 Escendant
//    cards (`spectral_tiger_champion` → spectral_tiger / champion). The
//    Necro Seal tag is stripped first (baseId), so a Resurrected Lord
//    still has a body. Anything else → null, and the caller keeps the
//    card slab.
//  · <FieldBeing>     — the body, standing on a point, facing the seam,
//    fitted to a per-tier stand height, and ALIVE (SHIP 8.2): it
//    materialises when it takes the field, rises gold out of an Ascend,
//    green out of a Resurrection.
//  · <GhostBody>      — a body's last moment (SHIP 8.2): the form an
//    Ascend leaves behind flashing out where it stood, or a ruined
//    being streaking into the Necropolis. One-shot, self-retiring.
//  · beingLife        — the bus the detector in Shatterfield3D writes
//    entrance kinds to, keyed by rift, so a body mounting on touchdown
//    knows whether it was summoned, raised or ascended. The fit is MEASURED off the
//    body's real mesh extents on first mount (auras and veils excluded),
//    cached per lineage|tier, and never hand-typed — the same rule Ruin
//    Rush's adapter arrived at after twelve guesses: a scale that is
//    derived corrects itself the day the kit re-authors a body.
//
// PERF LAW: no setState in useFrame; every per-frame write is on a ref.
// PRNG LAW: nothing random here — the kit seeds its own variety.
// ═══════════════════════════════════════════════════════════════════
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import {
  BeingMesh, TIERS, LINEAGE_IDS, BEING_KIT_VERSION,
} from "./ieBeingModels";
import { baseId } from "./necroSeal";
// TERROR TOONZ — a body with a sculpt stands as its glb, the painted kit
// body behind it (THE FAIL-SAFE LAW). Everything else is unchanged.
import { BeingBody, hasSculpt } from "./sculptedBeings";
// T17 — the dial rules here too. OFF means no entrance and no ghost ever
// mounts; QUICK runs the same curves faster.
import { getMotion, motionOn, motionRate, strikePose } from "./strikeLanguage";

// Boot stamp — a Terror that prints an old number did not take the last
// re-vendor (anti-drift law #4 in the kit's own header).
export const FIELD_BEING_KIT_VERSION = BEING_KIT_VERSION;

// ── CARD → BODY ───────────────────────────────────────────────────
const TIER_OF = { beb: TIERS.BEB, champion: TIERS.CHAMPION, lord: TIERS.LORD };
const CARD_RE = /^(.+)_(beb|champion|lord)$/;

export function beingOf(cardId) {
  if (!cardId || typeof cardId !== "string") return null;
  const m = CARD_RE.exec(baseId(cardId));
  if (!m || LINEAGE_IDS.indexOf(m[1]) === -1) return null;
  return { lineage: m[1], tier: TIER_OF[m[2]] };
}

// ── THE STAND ─────────────────────────────────────────────────────
// How tall each tier reads on the table, in world units. A card is
// 1.0 × 1.4 lying flat and the rifts sit 1.55 apart, so a Beb stands
// about two-thirds of a card-length and a Lord a full one. Ascension
// is STATION, not scale (ie-being-models §4e) — the kit's own bodies
// already grow up the ladder; these numbers only keep the whole roster
// readable in one frame. Three dials. Turn them, never the kit.
export const STAND_H = { [TIERS.BEB]: 0.80, [TIERS.CHAMPION]: 1.00, [TIERS.LORD]: 1.22 };
// The widest a body may stand, so a winged Lord never reaches into the
// next rift over (pitch 1.55, card 1.0 wide).
const MAX_FOOT = 1.30;
// The kit's bodies are authored facing +Z. Seat-local −Z is "toward
// the enemy" for BOTH chairs (the adversary's seat is π about Y), so
// one yaw serves both, the same way Card3D's lunge needs no sign.
//
// THE STANCE. The adversary's beings face the seam square-on — they
// are coming at you, and you see their faces. Your own would then show
// you nothing but tails, and the Beb's face is the whole point of the
// Beb — so yours stand a three-quarter turn toward the enemy: still
// squared at the seam, but with the eyes readable from your chair.
export const STANCE = { mine: Math.PI - 0.75, theirs: Math.PI };
const FACE_SEAM = STANCE.theirs;

// ── MEASURING A BODY ──────────────────────────────────────────────
// Local-space bounds of everything SOLID under `root`. Auras, veils,
// smoke rings and wisps are meshBasic + transparent + depthWrite:false
// (the kit's own gaseous-vs-solid rule, §4d) and the rim aura is a
// BackSide shell — all skipped, or a Spectral Tiger would be fitted to
// its own veil and stand a head shorter than the Shadow Fox.
// Corners are transformed mesh → world → root-local so a rotated part
// (a cocked head, an angled ear) is bounded exactly, not by an inflated
// axis-aligned box of an axis-aligned box.
const MEASURE = new Map();   // "lineage|tier" → { minY, h, w, d, cx, cz }
const _inv = new THREE.Matrix4();
const _m = new THREE.Matrix4();
const _v = new THREE.Vector3();
// (exported for SHIP 10's companion adapter — one measure for every pose)
export function measureSolid(root, includeGas = false) {
  root.updateMatrixWorld(true);
  _inv.copy(root.matrixWorld).invert();
  const box = new THREE.Box3();
  box.makeEmpty();
  root.traverse((o) => {
    if (!o.isMesh || !o.geometry) return;
    const mat = Array.isArray(o.material) ? o.material[0] : o.material;
    if (!includeGas) {
      if (mat && mat.transparent && mat.depthWrite === false) return;
      if (mat && mat.side === THREE.BackSide) return;
    }
    const g = o.geometry;
    if (!g.boundingBox) g.computeBoundingBox();
    const bb = g.boundingBox;
    if (!bb) return;
    _m.multiplyMatrices(_inv, o.matrixWorld);
    for (let i = 0; i < 8; i++) {
      _v.set(i & 1 ? bb.max.x : bb.min.x, i & 2 ? bb.max.y : bb.min.y, i & 4 ? bb.max.z : bb.min.z);
      _v.applyMatrix4(_m);
      box.expandByPoint(_v);
    }
  });
  // A body that is ALL veil (H-01 Super Nova is "a body you can see
  // through") would measure empty — take the gas too rather than stand
  // it at raw kit scale.
  if (box.isEmpty()) return includeGas ? null : measureSolid(root, true);
  return {
    minY: box.min.y,
    h: Math.max(1e-3, box.max.y - box.min.y),
    w: Math.max(1e-3, box.max.x - box.min.x),
    d: Math.max(1e-3, box.max.z - box.min.z),
    cx: (box.min.x + box.max.x) / 2,
    cz: (box.min.z + box.max.z) / 2,
  };
}

// The fit for one body: scale so the stand height lands, capped so the
// footprint stays inside its rift, then re-centred on x/z and lifted so
// its lowest solid point is exactly on the floor it was given.
export function standFit(mm, tier) {
  const s = Math.min(STAND_H[tier] / mm.h, MAX_FOOT / Math.max(mm.w, mm.d));
  return { s, x: -mm.cx * s, y: -mm.minY * s, z: -mm.cz * s };
}

// ── THE HIT PROXY ─────────────────────────────────────────────────
// A body is 40–120 meshes and its rim aura is a BackSide shell wider
// than the body. Letting the raycaster see all of that means every
// pointer move tests hundreds of extra objects and a hover "on the
// tiger" starts a hand's width outside its fur. So the kit's meshes are
// taken OUT of picking (`raycast` no-op'd, once, on fit) and ONE
// invisible box the size of the fitted body stands in for them. Pointer
// events bubble up the R3F tree, so a click on the proxy reaches the
// Card3D mesh the body rides on — the radial opens exactly as if the
// card had been tapped, which it effectively was.
const NO_RAYCAST = () => null;
const PROXY_MAT_PROPS = { transparent: true, opacity: 0, depthWrite: false, colorWrite: false };

// ── THE FIT, AS A HOOK ────────────────────────────────────────────
// Shared by the standing body and its ghost. Runs after the kit's meshes
// have committed and BEFORE the next paint (useLayoutEffect), so a Lord
// never shows one frame at raw kit scale. Returns the fit on a ref.
// `cacheable` is false while a sculpt-capable body has not yet said WHICH
// body is standing (sculpt or painted): that first measure is used, never
// stored, so the two bodies' boxes can never be filed under each other.
function useBodyFit(inner, proxy, info, key, cacheable = true) {
  const fitRef = useRef(null);
  useLayoutEffect(() => {
    const g = inner.current;
    fitRef.current = null;
    if (!g || !info) return;
    g.scale.setScalar(1);
    g.position.set(0, 0, 0);
    let mm = cacheable ? MEASURE.get(key) : null;
    if (!mm) {
      mm = measureSolid(g);
      if (mm && cacheable) MEASURE.set(key, mm);
    }
    if (!mm) return;
    const f = standFit(mm, info.tier);
    g.scale.setScalar(f.s);
    g.position.set(f.x, f.y, f.z);
    g.traverse((o) => { if (o.isMesh) o.raycast = NO_RAYCAST; });
    const px = proxy && proxy.current;
    if (px) {
      px.scale.set(Math.max(0.3, mm.w * f.s), Math.max(0.3, mm.h * f.s), Math.max(0.3, mm.d * f.s));
      px.position.set(0, (mm.h * f.s) / 2, 0);
    }
    fitRef.current = { ...f, h: mm.h * f.s, w: mm.w * f.s, d: mm.d * f.s };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return fitRef;
}

// ═══════════════════════════════════════════════════════════════════
// SHIP 8.2 — THE LIFE
// ═══════════════════════════════════════════════════════════════════
// ── THE BUS ───────────────────────────────────────────────────────
// The transition detector in Shatterfield3D knows WHY a body is about to
// mount (a summon flight is landing, a Resurrection is landing, an
// Ascend is landing); the body only knows THAT it mounted. So the
// detector leaves the kind here, keyed by rift ("m:esc:2"), and the body
// takes it on mount. Module-level, not context — context does not cross
// the <Canvas> boundary (the CAM_STATE / ATMOS pattern).
//
// `boardAt` is the guard against the board's OWN mount: a 2D↔3D toggle
// or a page load remounts every body at once, and a table of six beings
// all materialising is a story that did not happen.
export const beingLife = {
  boardAt: 0,
  pending: new Map(),
  boardMounted() { this.boardAt = Date.now(); this.pending.clear(); },
  mark(key, kind) { if (key) this.pending.set(key, { kind, at: Date.now() }); },
  take(key) {
    if (!key) return null;
    const e = this.pending.get(key);
    if (!e) return null;
    this.pending.delete(key);
    // A mark older than a few seconds is history, not an order — the
    // flight it was left for was cancelled, capped or outlived.
    return (Date.now() - e.at) < 4000 ? e.kind : null;
  },
  // SHIP 8.3 — THE KICKS. A live body reacting to something landing ON
  // it (an Impera branded, an U'hlti bound, an Affinity sworn, a Primera
  // pressed). Not a mount event, so not `pending`: the body polls this
  // each frame by its own key and starts a timeline when the stamp is
  // newer than the last one it played. One Map read per body per frame.
  kicks: new Map(),
  kick(key, kind) { if (key) this.kicks.set(key, { kind, at: Date.now() }); },
};

// The kicks — what a body does when a card lands on its Soul. `ms` at
// FULL. `rear` is how far it rises onto its heels, `arms` how wide it
// throws them, `hop` a jump for the little ones.
export const KICK = {
  brand: { ms: 520, rear: 0.34, arms: 0.55, hop: 0.10, grow: 1.10 },  // Impera — pressed in, it flexes
  sign:  { ms: 900, rear: 0.55, arms: 0.85, hop: 0.16, grow: 1.16 },  // U'hlti — the loudest thing on the table
  seal:  { ms: 700, rear: 0.10, arms: 0.20, hop: 0.00, grow: 0.94 },  // Affinity — settles inward, sworn
  stamp: { ms: 380, rear: 0.16, arms: 0.30, hop: 0.12, grow: 1.05 },  // Primera pressed — a quick hop
};

// The entrances. `ms` at FULL; QUICK divides by the dial's rate.
export const ENTRANCE = {
  summon: { ms: 620, color: "#fff2c8", rise: 0.28, spin: 0.35 },  // took the field
  ascend: { ms: 720, color: "#f0d060", rise: 0.12, spin: 0.0 },   // stood up taller
  revive: { ms: 900, color: "#4ade80", rise: 0.55, spin: 0.0 },   // came back
  descend:{ ms: 520, color: "#c8a0ff", rise: 0.05, spin: 0.0 },   // the lower form, standing again
};
const ease = {
  out: (t) => 1 - Math.pow(1 - t, 3),
  back: (t) => { const c = 1.70158; return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2); },
};

// ═══════════════════════════════════════════════════════════════════
// SHIP 8.3 — THE FIGHT
// ═══════════════════════════════════════════════════════════════════
// The card under a standing body no longer swings; the BODY swings. The
// same strike profiles Card3D has played since H21 (Space's Vector,
// Phantom's Fade, Dragon's Maul, Time's Echo, the Lunge) drive the whole
// body across the seam, and the kit's rig door (v1.1.0: every bound slot
// is named rig_head / rig_armL / rig_armR / rig_legL / rig_legR /
// rig_tail / rig_torso) lets the limbs join in from OUTSIDE the kit —
// they own the body, we own the pose.
//
// ⚠ TWO PIVOT CONVENTIONS (Ruin Rush's finding, racingBeingAdapter):
// some slots pivot at a joint with the mass hanging just below (an arm),
// others pivot at the BODY ORIGIN with the mass half a unit away (the
// tiger's legs). The same angle on the second tears the limb off. So the
// cap is on DISTANCE, not angle: a limb may swing its mass at most
// LIMB_TRAVEL kit-units, and the angle falls out as travel / radius.
const LIMB_TRAVEL = 0.34;
const RIG_SLOTS = ["head", "armL", "armR", "legL", "legR", "tail", "torso"];
// A limb's rest is re-read every frame the kit moves it, so a slot the
// kit idles (a tail sway, a nod) keeps its idle and gets our pose ADDED,
// while a slot the kit leaves alone keeps its authored rest exactly.
function findRig(root) {
  const rig = {};
  RIG_SLOTS.forEach((slot) => {
    const o = root.getObjectByName("rig_" + slot);
    if (!o) return;
    // mass radius: how far the slot's first mesh sits from the pivot
    let r = 0.2;
    const m = o.children.find((c) => c.isMesh) || o.children[0];
    if (m) r = Math.max(0.05, m.position.length());
    rig[slot] = {
      o, cap: Math.min(1.1, LIMB_TRAVEL / r),
      bx: o.rotation.x, bz: o.rotation.z, lx: o.rotation.x, lz: o.rotation.z,
    };
  });
  return rig;
}
// Write pose = kit's own value + ours, on one axis, without ever
// accumulating: if the kit moved the axis since our last write, that new
// value is the base; otherwise the base stands.
function poseAxis(st, axis, delta) {
  const o = st.o.rotation;
  const cur = o[axis];
  const lastK = axis === "x" ? "lx" : "lz", baseK = axis === "x" ? "bx" : "bz";
  if (cur !== st[lastK]) st[baseK] = cur;
  const v = st[baseK] + delta;
  o[axis] = v;
  st[lastK] = v;
}
const bump = (t) => Math.sin(Math.min(1, Math.max(0, t)) * Math.PI);

// ── THE BODY ON THE RIFT ──────────────────────────────────────────
// `position` is where the feet go (the top of whatever the being is
// standing on). `seed` offsets the idle so four beings in a row do not
// breathe in lockstep. `lifeKey` is the rift's key on the bus above.
// SHIP 8.3 — `strike` / `impact` / `shakeUntil` / `lunge` are the SAME
// fx entries SeatSide hands the card (Card3D stops moving for them when
// a body is riding it); `fallen` and `fractured` are the slot's states.
export function FieldBeing({ cardId, position = [0, 0, 0], seed = 0, stance = "theirs", yaw = null, lifeKey = null,
                             strike = null, impact = null, shakeUntil = 0, lunge = false,
                             fallen = false, fractured = false }) {
  const yawK = yaw != null ? yaw : (STANCE[stance] ?? FACE_SEAM);
  const info = beingOf(cardId);
  // `bodyKey` is WHICH being stands (the entrance keys on it); `key` adds
  // WHICH body it wears — "p" painted, "s" sculpted, "?" not told yet —
  // because the two have different boxes, so the fit and the rig re-run
  // on a swap. The word is filed WITH the body that said it: an Ascend
  // swaps the card under this same component, and the old body's word
  // must never be read as the new body's (that would cache one body's
  // box under the other's).
  const bodyKey = info ? `${info.lineage}|${info.tier}` : null;
  const sculptable = !!info && hasSculpt(info.lineage, info.tier);
  const [said, setSaid] = useState({ id: null, k: null });
  const onSkin = useCallback((k, id) => setSaid((o) => (o.id === id && o.k === k ? o : { id, k })), []);
  const skin = said.id === bodyKey ? said.k : (sculptable ? "?" : "p");
  const key = info ? `${bodyKey}|${skin}` : null;
  const outer = useRef();
  const inner = useRef();
  const proxy = useRef();
  const shell = useRef();
  const fitRef = useBodyFit(inner, proxy, info, key, skin !== "?");
  // the rig, found once per body (after the fit, same trigger)
  const rigRef = useRef({});
  useLayoutEffect(() => { rigRef.current = inner.current ? findRig(inner.current) : {}; }, [key]);
  // timelines — all on refs, all sampled per frame
  const strikeT = useRef({ at: 0, t: 1 });
  const kickT = useRef({ at: 0, t: 1, spec: null });
  const lungeK = useRef(0);
  const downK = useRef(0);   // fallen / fractured, eased so a state flip is a motion

  // ── THE ENTRANCE ────────────────────────────────────────────────
  // Decided once, on mount and again whenever the body changes under
  // the same rift: none if the whole board just mounted or the dial is
  // OFF; otherwise whatever the detector left on the bus, or a plain
  // summon. Everything after this line is imperative on refs.
  // ⚠ THE KIND IS DECIDED ON THE FIRST FRAME, NOT IN THIS EFFECT. The
  // detector that MARKS the bus runs in Shatterfield3D's passive effect,
  // which React runs AFTER every layout effect in the same commit — so a
  // body that swaps in-place (a Descent has no flight) would take the
  // bus here before the mark had landed and read as a plain summon. A
  // useFrame runs after all effects have flushed. The same move makes
  // StrictMode's synthetic double-invoke of this effect harmless: it only
  // arms `pending`, and the ONE take happens in the frame loop.
  const ent = useRef({ kind: null, at: 0, ms: 0, spec: null, pending: false });
  useLayoutEffect(() => {
    const live = motionOn(getMotion()) && (Date.now() - beingLife.boardAt) > 800;
    ent.current = { kind: null, at: 0, ms: 0, spec: null, pending: live };
    // Hidden until the first frame decides and starts the curve, or a
    // Lord flashes at full size for one frame before its rise.
    if (live && outer.current) outer.current.scale.setScalar(0.001);
    if (shell.current) shell.current.visible = false;
    // keyed on the BEING, not the body: a sculpt finishing its load (or the
    // fail-safe standing the painted body in) is not a new entrance
  }, [bodyKey, lifeKey]);

  useFrame((state, dt) => {
    const o = outer.current;
    if (!o) return;
    const t = state.clock.elapsedTime;
    const breathe = 1 + Math.sin(t * 1.7 + seed) * 0.012;
    const e = ent.current;
    if (e.pending) {
      e.pending = false;
      const kind = beingLife.take(lifeKey) || "summon";
      const spec = ENTRANCE[kind] || ENTRANCE.summon;
      e.kind = kind; e.spec = spec; e.at = Date.now(); e.ms = spec.ms / motionRate(getMotion());
      // the shell takes THIS entrance's colour — set here, imperatively,
      // never as a JSX prop read off a ref during render (one frame late)
      if (shell.current) { shell.current.material.color.set(spec.color); shell.current.visible = true; }
    }
    // THE ENTRANCE — composed as terms like everything else below, so a
    // wound or a kick landing in the same tick is not swallowed by it.
    let esc = 1, erise = 0, espin = 0;
    if (e.kind) {
      const k = Math.min(1, (Date.now() - e.at) / e.ms);
      const sp = e.spec;
      esc = k < 1 ? Math.max(0.001, ease.back(k)) : 1;       // overshoot, then settle
      erise = (1 - ease.out(k)) * -sp.rise;                    // rises out of the card
      espin = (1 - ease.out(k)) * sp.spin;
      // the spark shell: a bright skin that swells past the body and fades
      const sh = shell.current;
      if (sh) {
        const f = fitRef.current;
        const r = f ? Math.max(f.w, f.h, f.d) * 0.62 : 0.5;
        sh.scale.setScalar(r * (0.85 + k * 0.6));
        sh.position.y = f ? f.h / 2 : 0.3;
        sh.material.opacity = (1 - k) * 0.75;
        sh.visible = k < 1;
      }
      if (k >= 1) e.kind = null;
    }
    // ── SHIP 8.3 — THE FIGHT, composed ─────────────────────────────
    // ONE WRITER: every term below adds into px/py/pz/rx/rz/sc and the
    // write happens once at the end. Add the next effect as a term,
    // never as a branch.
    const now = Date.now();
    const rate = motionRate(getMotion());
    const rig = rigRef.current;
    let px = position[0], py = position[1], pz = position[2];
    let rx = 0, rz = 0, sc = 1, sy = breathe;
    let head = 0, arms = 0, legs = 0, torso = 0, tail = 0, armsZ = 0;

    // THE STRIKE — the H21 profile, sampled here instead of on the card.
    // `fwd` is seat-local −z ("toward the enemy" for both chairs); the
    // body crosses more of the seam than a card did (BODY_REACH).
    const sT = strikeT.current;
    if (strike && strike.at && strike.at !== sT.at) {
      sT.at = strike.at;
      // the remount guard, as Card3D. ⚠ Every ENTRANCE.ms must stay under
      // this window: a strike in the same tick as a summon plays once the
      // entrance is over, and this is what decides it is not stale.
      sT.t = (now - strike.at) < 1200 ? 0 : 1;
    }
    if (strike && sT.t < 1) {
      const span = Math.max(0.08, ((strike.profile?.ms || 380) / 1000) / (strike.rate || 1));
      sT.t = Math.min(1, sT.t + Math.min(dt || 0.016, 0.05) / span);
      const sp = strikePose(strike.profile, sT.t, strike.profile?.power || 1);
      const BODY_REACH = 1.55;
      pz -= sp.fwd * BODY_REACH; py += sp.up * 1.2; px += sp.lat;
      rx += -sp.pitch * 1.3;            // lean INTO the swing (kit faces local +z)
      sc *= sp.scale;
      const k = Math.max(0, Math.min(1, sp.fwd / 0.7));
      head += 0.35 * k; arms -= 1.0 * k; legs -= 0.5 * k; torso += 0.18 * k;
      tail += 0.4 * Math.sin(sT.t * Math.PI * 2);
    }

    // THE LUNGE — a declared attacker, sustained: forward, crouched, set.
    lungeK.current += ((lunge ? 1 : 0) - lungeK.current) * 0.12;
    const lk = lungeK.current;
    if (lk > 0.0015) { pz -= lk * 0.30; rx += lk * 0.22; sy *= 1 - lk * 0.08; head += 0.2 * lk; torso += 0.1 * lk; }

    // THE WOUND — the victim's own impact profile (a Dragon gets rocked,
    // a Phantom barely registers): a recoil away from the seam and a
    // head-snap, decaying with the card's shake window.
    if (shakeUntil && now < shakeUntil) {
      const span = impact?.ms || 550;
      const k = Math.max(0, Math.min(1, (shakeUntil - now) / span));
      const mag = (impact?.mag ?? 0.05) * 6;
      pz += mag * k; rx -= 0.35 * k; head -= 0.4 * k; torso -= 0.2 * k;
      px += Math.sin(t * (impact?.freq ?? 66)) * mag * 0.4 * k;
    }

    // THE KICK — something landed on this being's Soul (or a seal was
    // pressed): rear up, throw the arms, settle. Polled off the bus.
    const kk = beingLife.kicks.get(lifeKey);
    const kT = kickT.current;
    if (kk && kk.at !== kT.at) { kT.at = kk.at; kT.t = (now - kk.at) < 1500 ? 0 : 1; kT.spec = KICK[kk.kind] || KICK.brand; }
    if (kT.spec && kT.t < 1) {
      kT.t = Math.min(1, kT.t + Math.min(dt || 0.016, 0.05) / ((kT.spec.ms / 1000) / rate));
      const b = bump(kT.t);
      rx -= kT.spec.rear * b; py += kT.spec.hop * b; sc *= 1 + (kT.spec.grow - 1) * b;
      armsZ += kT.spec.arms * b; head -= 0.25 * b; torso -= 0.12 * b;
    }

    // FALLEN / FRACTURED — eased, so the state flipping is a motion, not
    // a cut. Fallen (descended, impervious, not a wall) goes down on its
    // knees and forward; Fractured (Lyf 0, still standing) hunches and
    // trembles — one more Lyt Attack ruins it, and it looks like it.
    const want = fallen ? 1 : fractured ? 0.45 : 0;
    downK.current += (want - downK.current) * 0.08;
    const dk = downK.current;
    if (dk > 0.002) {
      if (fallen) { rx += 0.55 * dk; sy *= 1 - 0.30 * dk; py -= 0.02 * dk; head += 0.5 * dk; legs -= 0.6 * dk; }
      else { rx += 0.18 * dk; sy *= 1 - 0.10 * dk; px += Math.sin(t * 23 + seed) * 0.012 * dk; head += 0.25 * dk; }
    }

    o.position.set(px, py + erise, pz);
    o.rotation.set(rx, yawK + espin, rz);
    o.scale.set(sc * esc, sc * esc * sy, sc * esc);
    // the limbs — through the rig door, capped by travel, never accumulating
    if (rig.head) poseAxis(rig.head, "x", head * rig.head.cap);
    if (rig.torso) poseAxis(rig.torso, "x", torso);
    if (rig.armL) { poseAxis(rig.armL, "x", arms * rig.armL.cap); poseAxis(rig.armL, "z", armsZ * rig.armL.cap); }
    if (rig.armR) { poseAxis(rig.armR, "x", arms * rig.armR.cap); poseAxis(rig.armR, "z", -armsZ * rig.armR.cap); }
    if (rig.legL) poseAxis(rig.legL, "x", legs * rig.legL.cap);
    if (rig.legR) poseAxis(rig.legR, "x", legs * rig.legR.cap);
    if (rig.tail) poseAxis(rig.tail, "z", tail * rig.tail.cap);
  });

  if (!info) return null;
  return (
    <group ref={outer} position={position} rotation={[0, yawK, 0]}>
      <group ref={inner}>
        <BeingBody lineage={info.lineage} tier={info.tier} onSkin={onSkin} />
      </group>
      <mesh ref={proxy} position={[0, 0.3, 0]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshBasicMaterial {...PROXY_MAT_PROPS} />
      </mesh>
      {/* the spark shell — BackSide so it reads as a skin of light around
          the body, not a ball in front of it; no depth write so it never
          hides the being it is announcing */}
      <mesh ref={shell} visible={false} raycast={NO_RAYCAST}>
        <sphereGeometry args={[1, 18, 14]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={0} side={THREE.BackSide}
          depthWrite={false} toneMapped={false} />
      </mesh>
    </group>
  );
}

// ── THE GHOST ─────────────────────────────────────────────────────
// A body's last moment. The same kit body, fitted the same way, but every
// material swapped for one flash skin — the being as pure light — then
// driven along `from → to` and faded, and gone. Two uses, one component:
//   · an Ascend: from = to = the rift. The lower form flashes out where it
//     stood while the higher card is still in the air (the being did not
//     leave; it got better — RULEZ_V2: the lower form goes into the Soul).
//   · a ruin: from = the rift, to = the Necropolis. The being streaks
//     into the ground it is about to be buried in, shrinking as it goes.
// World-space: the detector mounts these beside the FlightCards, so the
// caller passes world coordinates and a world yaw.
export const GHOST = {
  ascend: { ms: 820, color: "#f0d060", rise: 0.5, endScale: 1.18, arc: 0 },
  ruin:   { ms: 760, color: "#c8506a", rise: 0.0, endScale: 0.30, arc: 0.7 },
};
export function GhostBody({ cardId, from, to = null, yaw = 0, kind = "ruin", onDone }) {
  const info = beingOf(cardId);
  // the ghost is always the PAINTED body (a flash-skinned silhouette for
  // under a second) — it shares the painted body's fit
  const key = info ? `${info.lineage}|${info.tier}|p` : null;
  const outer = useRef();
  const inner = useRef();
  useBodyFit(inner, null, info, key);
  const spec = GHOST[kind] || GHOST.ruin;
  // One flash material PER MESH, not one shared: the kit's own idles keep
  // writing `mesh.material.opacity` / `.emissiveIntensity` on their refs
  // every frame (a halo breathing, a stole shimmering), and a shared skin
  // would have its fade overwritten by whichever part wrote last. Clones
  // take those writes harmlessly and the fade below re-sets every clone
  // each frame AFTER the kit has run (children subscribe to useFrame
  // before their parent, so this component's frame runs last).
  const skins = useRef([]);
  // ⚠ Born on the FIRST FRAME, not at render: mounting a kit body (its
  // canvases, its meshes) can take longer than the whole ghost lasts at
  // QUICK, and a clock started at render then reads "finished" before
  // a single frame has drawn it.
  const born = useRef(0);
  const ms = spec.ms / motionRate(getMotion());
  const done = useRef(false);

  // The flash skin — every mesh of the kit body wears its own clone.
  // This instance is a separate mount of BeingMesh (its own materials),
  // so nothing standing on the field is touched.
  useLayoutEffect(() => {
    const g = inner.current;
    if (!g) return undefined;
    const made = [];
    g.traverse((o) => {
      if (!o.isMesh) return;
      const m = new THREE.MeshBasicMaterial({
        color: spec.color, transparent: true, opacity: 0.9, depthWrite: false, toneMapped: false,
      });
      o.material = m;
      made.push(m);
    });
    skins.current = made;
    return () => { made.forEach((m) => m.dispose()); skins.current = []; };
  }, [key, spec.color]);

  useFrame(() => {
    const o = outer.current;
    if (!o || done.current) return;
    if (!born.current) born.current = Date.now();
    const k = Math.min(1, (Date.now() - born.current) / ms);
    const kk = ease.out(k);
    const dst = to || from;
    const x = from[0] + (dst[0] - from[0]) * kk;
    const z = from[2] + (dst[2] - from[2]) * kk;
    const y = from[1] + (dst[1] - from[1]) * kk + Math.sin(k * Math.PI) * spec.arc + kk * spec.rise;
    o.position.set(x, y, z);
    const sc = 1 + (spec.endScale - 1) * kk;
    o.scale.setScalar(Math.max(0.001, sc));
    const op = 0.9 * (1 - k * k);
    const sk = skins.current;
    for (let i = 0; i < sk.length; i++) sk[i].opacity = op;
    if (k >= 1) { done.current = true; onDone && onDone(); }
  });

  if (!info) return null;
  return (
    <group ref={outer} position={from} rotation={[0, yaw, 0]}>
      <group ref={inner}>
        <BeingMesh beingId={info.lineage} tier={info.tier} />
      </group>
    </group>
  );
}

export default FieldBeing;
